import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Globe, CircleDot, CheckCircle2, ChevronDown, ChevronUp, ArrowUpRight } from "lucide-react";
import EngagementBadge from "@/components/EngagementBadge";
import SessionMetaRow from "@/components/SessionMetaRow";

// Phase 25.13aa — Country drill-through. Mirrors ReferrerSessionsDrawer
// but keyed on country code (e.g. SG → "Singapore"). Lets super-admin
// click a row in the // COUNTRIES panel and see exactly what those
// visitors viewed.
//
// Props:
//   country (string): ISO-2 country code (e.g. "SG", "AU")
//   days (number): lookback window
//   includeInternal (bool): mirrors AdminVisitors INTERNAL toggle
//   onClose ()

// Minimal ISO-2 → human-readable map. We only need the ones that have
// actually shown up in the dashboard; unknowns fall back to the raw code.
const COUNTRY_NAMES = {
  AU: "Australia",
  US: "United States",
  GB: "United Kingdom",
  SG: "Singapore",
  CA: "Canada",
  NZ: "New Zealand",
  IN: "India",
  FR: "France",
  DE: "Germany",
  IE: "Ireland",
  NG: "Nigeria",
  ZA: "South Africa",
  BR: "Brazil",
  AE: "United Arab Emirates",
  JP: "Japan",
  HK: "Hong Kong",
  CN: "China",
  KR: "South Korea",
  PH: "Philippines",
  MY: "Malaysia",
  ID: "Indonesia",
  TH: "Thailand",
  VN: "Vietnam",
  MX: "Mexico",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  KE: "Kenya",
  EG: "Egypt",
  MA: "Morocco",
  TR: "Turkey",
  RU: "Russia",
  UA: "Ukraine",
  PL: "Poland",
  ES: "Spain",
  IT: "Italy",
  PT: "Portugal",
  NL: "Netherlands",
  BE: "Belgium",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  CH: "Switzerland",
  AT: "Austria",
  CZ: "Czechia",
  RO: "Romania",
  IL: "Israel",
  SA: "Saudi Arabia",
  PK: "Pakistan",
  BD: "Bangladesh",
  LK: "Sri Lanka",
};

function countryLabel(code) {
  if (!code) return "(unknown)";
  if (code === "(unknown)") return "Unknown";
  return COUNTRY_NAMES[code.toUpperCase()] || code;
}

export default function CountrySessionsDrawer({ country, days = 30, includeInternal = false, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!country) return undefined;
    let cancel = false;
    setLoading(true);
    const qs = new URLSearchParams({
      country,
      days: String(days),
      include_internal: includeInternal ? "true" : "false",
    }).toString();
    api.get(`/admin/visitors/country-sessions?${qs}`)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ sessions: [], session_count: 0, converted_count: 0, total_pageviews: 0 }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [country, days, includeInternal]);

  useEffect(() => {
    if (!country) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [country, onClose]);

  if (!country) return null;

  const human = countryLabel(country);

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Visitor sessions from ${human}`}
      data-testid="country-sessions-drawer"
    >
      <div
        className="relative bg-[#040404] border-l border-[#FFB000]/30 w-full max-w-2xl h-full overflow-y-auto shadow-[0_0_60px_-10px_rgba(255,176,0,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* terminal header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-900 bg-[#050505]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-2 h-2 bg-[#FF3B30]" />
            <div className="w-2 h-2 bg-[#FFB000]" />
            <div className="w-2 h-2 bg-[#00E676]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 truncate">
              DARA://sessions · country · <span className="text-[#FFB000]">{country}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="country-drawer-close"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1 transition-colors"
            aria-label="Close drawer"
          >
            CLOSE <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-2">
            // VISITORS FROM THIS COUNTRY · LAST {days}D
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-1 break-words flex items-center gap-3">
            <Globe className="w-6 h-6 text-[#FFB000]" />
            {human}
          </h2>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mb-5">
            ISO · {country}
          </div>

          {loading && (
            <div className="font-mono text-[11px] text-zinc-600 py-8 text-center">Loading…</div>
          )}

          {!loading && data && (
            <>
              <div className="grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 mb-6">
                <div className="bg-black p-4">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Unique sessions</div>
                  <div className="font-mono text-2xl text-white mt-1" data-testid="country-session-count">{data.session_count}</div>
                </div>
                <div className="bg-black p-4">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Pageviews</div>
                  <div className="font-mono text-2xl text-[#FFB000] mt-1">{data.total_pageviews ?? 0}</div>
                </div>
                <div className="bg-black p-4">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Converted</div>
                  <div className={`font-mono text-2xl mt-1 ${data.converted_count > 0 ? "text-[#00E676]" : "text-zinc-600"}`}>
                    {data.converted_count}
                  </div>
                </div>
              </div>

              {data.sessions.length === 0 ? (
                <div className="font-mono text-[11px] text-zinc-600 leading-relaxed py-8 text-center border border-dashed border-zinc-900">
                  No sessions from this country in the last {days} days, or all sessions are filtered out by the INTERNAL toggle.
                </div>
              ) : (
                <ul className="space-y-2">
                  {data.sessions.map((s, idx) => {
                    const isOpen = expanded === s.session_id;
                    const firstDay = (s.first_seen || "").slice(0, 10);
                    const lastDay = (s.last_seen || "").slice(0, 10);
                    return (
                      <li key={s.session_id} className="border border-zinc-900 bg-[#070707]" data-testid={`country-session-${idx}`}>
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : s.session_id)}
                          className="w-full text-left px-4 py-3 hover:bg-zinc-950/50"
                          aria-expanded={isOpen}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-mono text-[9px] text-zinc-700 w-6">{String(idx + 1).padStart(2, "0")}</span>
                              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                <span className="font-mono text-[11px] text-zinc-300 truncate">{s.landing_path}</span>
                                {s.engagement && <EngagementBadge engagement={s.engagement} compact />}
                                {s.landing_referrer && s.landing_referrer !== "(direct)" && (
                                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 flex-shrink-0">
                                    <ArrowUpRight className="w-2.5 h-2.5" /> {s.landing_referrer}
                                  </span>
                                )}
                                {s.converted && (
                                  <span className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] flex items-center gap-1 flex-shrink-0 border border-[#00E676]/40 bg-[#00E676]/5 px-1.5 py-px">
                                    <CheckCircle2 className="w-2.5 h-2.5" /> CONVERTED
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="font-mono text-[10px] text-[#FFB000]">{s.pageviews} pv</span>
                              {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                            </div>
                          </div>
                          <div className="pl-8">
                            <SessionMetaRow session={s} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="border-t border-zinc-900 px-4 py-3 bg-black">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-2">
                              Journey · {firstDay}{lastDay !== firstDay ? ` → ${lastDay}` : ""}
                            </div>
                            <ol className="space-y-1.5">
                              {s.journey.map((step, j) => (
                                <li key={j} className="flex items-center gap-2 font-mono text-[11px] text-zinc-400">
                                  <CircleDot className="w-2.5 h-2.5 text-[#FFB000] flex-shrink-0" />
                                  <span className="truncate">{step.path}</span>
                                  <span className="text-zinc-700 ml-auto">{(step.created_at || "").slice(11, 19)}Z</span>
                                </li>
                              ))}
                            </ol>
                            <div className="mt-3 pt-3 border-t border-zinc-900 font-mono text-[9px] uppercase tracking-widest text-zinc-700 break-all">
                              session_id: {s.session_id}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {data.sessions.length > 0 && data.session_count > data.sessions.length && (
                <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-600 text-center">
                  Showing {data.sessions.length} of {data.session_count}.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
